/**
 * Mileage view — trips table with the IRS-rate KPI sourced live from Settings.
 */
import { DB } from "../lib/db";
import { slicePage, paginationControlsHtml } from "../utils/pagination";
import { icon } from "../utils/icon";
import { createRowSelection } from "../components/rowSelection";
import { Lists, Settings } from "../lib/prefs";
import { escapeHtml, toast, $ } from "../lib/dom";
import { fmt$, fmt$0, fmtN, fmtDate, sum, kpiBar, todayISO } from "../utils/format";
import { openModal, closeModal } from "../components/modal";
import { formField, readForm } from "../components/formField";
import { wireLotSearch } from "../components/lotSearch";
import { render } from "../state";
import type { Row } from "../lib/types";

const sel = createRowSelection("mileage");
function mileageEdit(uuid: string): void { (window as any).editMileage?.(uuid); }
function mileageDelete(uuid: string): void { (window as any).deleteMileage?.(uuid); }

function findMileageByUuid(uuid: string): Row | null {
  if (!uuid) return null;
  return DB.table("mileage").find(r => r && r._uuid === uuid) || null;
}

export function viewMileage(root: HTMLElement): void {
  const m = DB.table("mileage").slice().sort((a, b) =>
    String(b["Date"] || "").localeCompare(String(a["Date"] || ""))
  );
  const totalMiles = sum(m, r => r["Miles"]);
  const totalDed   = sum(m, r => r["Deduction"]);
  const irsRate    = Settings.get().irsRate;

  const m_total = m.length;
  const m_page = slicePage("mileage", m, 50);
  root.innerHTML = `
    ${kpiBar([
      { label: "Trips",     value: fmtN(m.length) },
      { label: "Miles YTD", value: fmtN(totalMiles) },
      { label: "Deduction", value: fmt$0(totalDed) },
      { label: "IRS Rate",  value: "$" + irsRate.toFixed(2) + "/mi" },
    ])}
    <div class="panel p-3 mb-3 flex flex-wrap gap-2 items-center">
      ${sel.toolbarHtml([
        { id: "edit",   label: `${icon("edit")} Edit`,   title: "Edit selected row", handler: mileageEdit },
        { id: "delete", label: `${icon("trash")} Delete`, title: "Delete selected row", danger: true, handler: mileageDelete },
      ])}
    </div>
    <div class="panel scrollx">
      <table class="t">
        <thead><tr>
          <th>Date</th><th>Purpose</th>
          <th class="text-right">Start Odo</th><th class="text-right">End Odo</th>
          <th class="text-right">Miles</th><th class="text-right">Rate</th>
          <th class="text-right">Deduction</th><th>Related</th><th>Notes</th>
        </tr></thead>
        <tbody>
          ${m_page.map(r => {
            const u = escapeHtml(r._uuid || "");
            return `<tr ${sel.rowAttrs(u, `editMileage('${u}')`)}>
              <td>${escapeHtml(fmtDate(r["Date"]))}</td>
              <td>${escapeHtml(r["Purpose"] || "")}</td>
              <td class="text-right stat-num">${fmtN(r["Start Odo"])}</td>
              <td class="text-right stat-num">${fmtN(r["End Odo"])}</td>
              <td class="text-right stat-num">${fmtN(r["Miles"])}</td>
              <td class="text-right stat-num">${fmt$(r["IRS Rate"])}</td>
              <td class="text-right stat-num">${fmt$(r["Deduction"])}</td>
              <td class="font-mono text-xs">${escapeHtml(r["Related Deal/Lot"] || "")}</td>
              <td class="text-xs text-[var(--muted)]" title="${escapeHtml(r["Notes"] || "")}">${escapeHtml(r["Notes"] || "")}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="9" class="text-center text-[var(--muted)] py-8">No trips logged.</td></tr>`}
        </tbody>
      </table>
    </div>
    ${paginationControlsHtml("mileage", m_total, 50)}
  `;
  sel.applyUI();
}

/* ============================================================
   Mileage form (editMileage / saveMileage / deleteMileage)
   ============================================================ */
export async function editMileage(uuid?: string): Promise<void> {
  const isNew = !uuid;
  // Pull lot-bearing tables fresh so the related-lot search reflects cloud-
  // current values. Also refresh mileage on edit.
  {
    const refreshes: Promise<void>[] = [DB.refreshTable("sealed"), DB.refreshTable("cards"), DB.refreshTable("trades"), DB.refreshTable("purchases")];
    if (!isNew) refreshes.push(DB.refreshTable("mileage"));
    await Promise.all(refreshes);
  }
  const row = isNew
    ? { "Date": todayISO(), "IRS Rate": Settings.get().irsRate }
    : findMileageByUuid(uuid!);
  if (!row) { toast("Trip not found", "err"); return; }

  const body = `
    <form id="mileageForm" class="grid grid-cols-1 sm:grid-cols-2 gap-3" onsubmit="return false;">
      ${formField({ label: "Date", name: "Date", type: "date", value: fmtDate(row["Date"]), required: true })}
      ${formField({ label: "Purpose", name: "Purpose", value: row["Purpose"], options: Lists.get("Mileage Purpose") })}
      ${formField({ label: "Start Odometer", name: "Start Odo", type: "number", step: "1", value: row["Start Odo"] })}
      ${formField({ label: "End Odometer", name: "End Odo", type: "number", step: "1", value: row["End Odo"] })}
      ${formField({ label: "Miles (auto if blank)", name: "Miles", type: "number", step: "0.1", value: row["Miles"] })}
      ${formField({ label: "IRS Rate ($/mi)", name: "IRS Rate", type: "number", step: "0.001", value: row["IRS Rate"] })}
      <div>
        <label class="label" for="f_Related_Deal_Lot">Related Deal/Lot</label>
        <div class="combobox-wrapper" data-mileage-lot>
          <input class="input" id="f_Related_Deal_Lot" name="Related Deal/Lot" autocomplete="off"
                 placeholder="Search a lot, trade, or purchase — or type any reference…"
                 value="${escapeHtml(row["Related Deal/Lot"] || "")}">
          <div class="combobox-list"></div>
        </div>
        <div class="text-xs text-[var(--muted)] mt-1">Tag this trip to a card/sealed lot, a trade, or a purchase — or type free text.</div>
      </div>
      <div class="sm:col-span-2">${formField({ label: "Notes", name: "Notes", type: "textarea", value: row["Notes"] })}</div>
    </form>`;
  const u = isNew ? "" : escapeHtml(uuid!);
  const footer = `
    ${!isNew ? `<button class="btn btn-danger" onclick="deleteMileage('${u}')">Delete</button>` : ""}
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveMileage('${u}')">${isNew ? "Add" : "Save"}</button>`;
  openModal({ title: isNew ? "Add Trip" : "Edit Trip", body, footer });

  // "Related Deal/Lot" is a searchable picker over existing card/sealed lots.
  // Selecting one fills the field with its Lot ID; free text is still allowed.
  const lotWrap = document.querySelector<HTMLElement>("[data-mileage-lot]");
  if (lotWrap) {
    wireLotSearch(lotWrap, {
      kind: "any",
      includeDeals: true,
      onSelect: (lot) => {
        const inp = lotWrap.querySelector<HTMLInputElement>("input");
        if (inp) inp.value = lot.id;
      },
    });
  }

  // Auto-fill Miles = End Odo − Start Odo as the odometer fields are typed.
  // The Miles field stays user-editable: typing in it stops the auto-fill;
  // clearing it resumes. An existing trip whose Miles doesn't match the
  // odometer delta is treated as a manual override and left untouched.
  const startInp = document.querySelector<HTMLInputElement>('#mileageForm [name="Start Odo"]');
  const endInp   = document.querySelector<HTMLInputElement>('#mileageForm [name="End Odo"]');
  const milesInp = document.querySelector<HTMLInputElement>('#mileageForm [name="Miles"]');
  if (startInp && endInp && milesInp) {
    let milesManual = false;
    const initS = parseFloat(startInp.value);
    const initE = parseFloat(endInp.value);
    const initM = parseFloat(milesInp.value);
    if (Number.isFinite(initM)) {
      milesManual = !(Number.isFinite(initS) && Number.isFinite(initE)
        && Math.abs(+(initE - initS).toFixed(1) - initM) < 1e-9);
    }
    const recompute = () => {
      if (milesManual) return;
      const s = parseFloat(startInp.value);
      const e = parseFloat(endInp.value);
      if (Number.isFinite(s) && Number.isFinite(e)) milesInp.value = String(+(e - s).toFixed(1));
    };
    startInp.addEventListener("input", recompute);
    endInp.addEventListener("input", recompute);
    milesInp.addEventListener("input", () => {
      milesManual = milesInp.value.trim() !== "";
      if (!milesManual) recompute();
    });
  }
}

export async function saveMileage(uuid: string): Promise<void> {
  const d = readForm($("#mileageForm"));
  // Auto-calc miles when both odometer readings are present and miles is blank.
  if (!d["Miles"] && d["Start Odo"] != null && d["End Odo"] != null) {
    d["Miles"] = +(d["End Odo"] - d["Start Odo"]).toFixed(1);
  }
  d["Deduction"] = +((Number(d["Miles"]) || 0) * (Number(d["IRS Rate"]) || 0)).toFixed(2);
  try {
    if (!uuid) await DB.add("mileage", d);
    else       await DB.updateByUuid("mileage", uuid, d);
  } catch (e: any) {
    console.error("[saveMileage]", e);
    toast("Cloud sync failed: " + (e?.message || e), "err");
    return;
  }
  toast("Trip saved");
  closeModal();
  render();
}

export async function deleteMileage(uuid: string): Promise<void> {
  if (!uuid) return;
  if (!confirm("Delete this trip?")) return;
  try { await DB.removeByUuid("mileage", uuid); }
  catch (e: any) {
    console.error("[deleteMileage]", e);
    toast("Cloud delete failed: " + (e?.message || e), "err");
    return;
  }
  toast("Deleted");
  closeModal();
  render();
}

// Inline-handler bridges
(window as any).editMileage = editMileage;
(window as any).saveMileage = saveMileage;
(window as any).deleteMileage = deleteMileage;
